import React, { useState, useMemo } from 'react'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import type { ReconciliationRowVO, ReconciliationIssueType } from '../../../apis/types'
import { fieldColumns, sourceDisplayNames, displayString } from './reconciliation.types'

interface ReconciliationActionChecklistProps {
    rows: ReconciliationRowVO[]
}

function issueAction(issue: ReconciliationIssueType, row: ReconciliationRowVO): string | null {
    switch (issue) {
        case 'scraperNotLinked':
            return `Link utility account ${row.utilityAccountNumber} to the scraper in Program portal settings`
        case 'missingServiceAddress':
            return 'Service address missing in Utility Portal \u2014 scraper may not have captured it, or enter manually in Admin'
        case 'missingVnemAllocation':
            return `No VNEM allocation found for meter ${row.meterNumber ?? row.utilityAccountNumber} \u2014 Verify if this meter should be included or excluded from the VNEM table.`
        case 'inactiveMeter':
            return null
        case 'serviceIdMismatch':
            return 'Meter number doesn\u2019t match between Admin and Utility Portal \u2014 verify which is correct'
        case 'returnedPayment':
            return 'Last payment was returned \u2014 follow up with tenant about payment status'
        case 'accountNameMismatch':
            return 'Account name doesn\'t match between sources \u2014 verify which is correct'
        case 'billingAddressMismatch':
            return 'Billing address mismatch \u2014 verify in the Utility Portal'
        case 'serviceIdPlaceholder':
            return 'Service ID is a placeholder \u2014 update with the real service agreement number'
        default:
            return `${issue} detected \u2014 investigate`
    }
}

interface AccountActions {
    accountName: string | null
    utilityAccountNumber: string
    actions: string[]
}

function buildChecklist(rows: ReconciliationRowVO[]): AccountActions[] {
    const grouped = new Map<string, AccountActions>()

    for (const row of rows) {
        let entry = grouped.get(row.utilityAccountNumber)
        if (!entry) {
            entry = {
                accountName: displayString(row.fields.accountName),
                utilityAccountNumber: row.utilityAccountNumber,
                actions: [],
            }
            grouped.set(row.utilityAccountNumber, entry)
        }

        // Check accountName mismatch explicitly
        const acctField = row.fields.accountName
        if (acctField?.status === 'mismatch' && acctField.mismatch) {
            const authSource = sourceDisplayNames[acctField.authoritativeSource] ?? acctField.authoritativeSource
            const otherSources = Object.keys(acctField.values).filter(s => s !== acctField.authoritativeSource)
            const nonAuthSource = otherSources[0] ? (sourceDisplayNames[otherSources[0] as keyof typeof sourceDisplayNames] ?? otherSources[0]) : 'the other source'
            entry.actions.push(`Update Account Name in ${nonAuthSource} to match ${authSource}: ${acctField.mismatch.authoritativeValue ?? '(empty)'}`)
        }

        // Collect actions from field mismatches
        for (const col of fieldColumns) {
            const field = row.fields[col.fieldKey]
            if (field.status === 'mismatch' && field.mismatch) {
                const authSource = sourceDisplayNames[field.authoritativeSource] ?? field.authoritativeSource
                // Determine the non-authoritative source from the field values
                const otherSources = (Object.keys(field.values) as Array<keyof typeof field.values>)
                    .filter((s) => s !== field.authoritativeSource)
                const nonAuthKey = otherSources[0]
                const nonAuthSource = nonAuthKey ? (sourceDisplayNames[nonAuthKey] ?? nonAuthKey) : 'the other source'
                const authValue = field.mismatch.authoritativeValue ?? '(empty)'
                entry.actions.push(
                    `Update ${col.label} in ${nonAuthSource} to match ${authSource}: ${authValue}`,
                )
            }
        }

        // Collect actions from row issues
        for (const issue of row.issues) {
            const action = issueAction(issue, row)
            if (action) {
                entry.actions.push(action)
            }
        }
    }

    // Deduplicate actions within each account and filter out empty
    const result: AccountActions[] = []
    for (const entry of grouped.values()) {
        entry.actions = [...new Set(entry.actions)]
        if (entry.actions.length > 0) {
            result.push(entry)
        }
    }
    return result
}

function checklistToMarkdown(checklist: AccountActions[]): string {
    return checklist.map((acct) => {
        const header = `### ${acct.accountName || 'Unknown Account'} (${acct.utilityAccountNumber})`
        const items = acct.actions.map((a) => `- [ ] ${a}`)
        return [header, ...items].join('\n')
    }).join('\n\n')
}

const ReconciliationActionChecklist: React.FC<ReconciliationActionChecklistProps> = ({ rows }) => {
    const [copied, setCopied] = useState(false)
    const checklist = useMemo(() => buildChecklist(rows), [rows])
    const totalActions = useMemo(() => checklist.reduce((sum, acct) => sum + acct.actions.length, 0), [checklist])

    if (totalActions === 0) return null

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(checklistToMarkdown(checklist))
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // silently fail
        }
    }

    return (
        <div className="mt-4">
            <Disclosure defaultOpen={false}>
                {({ open }) => (
                    <>
                        <DisclosureButton className="flex w-full items-center justify-between rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 px-4 py-3 text-left text-sm font-medium text-indigo-900 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 focus:outline-none">
                            <span>
                                Action Checklist{' '}
                                <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200">
                                    {totalActions}
                                </span>
                            </span>
                            <ChevronDownIcon className={`w-5 h-5 text-indigo-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </DisclosureButton>
                        <DisclosurePanel className="border border-t-0 border-indigo-200 dark:border-indigo-800 rounded-b-lg bg-white dark:bg-slate-900">
                            <div className="px-4 py-3 bg-indigo-50 dark:bg-indigo-900/10 border-b border-indigo-200 dark:border-indigo-800 flex items-center justify-between">
                                <p className="text-xs text-indigo-700 dark:text-indigo-300">
                                    Remediation actions generated from mismatches and issues. Checkboxes are for visual tracking only and are not saved.
                                </p>
                                <button
                                    onClick={handleCopy}
                                    className="ml-4 shrink-0 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200"
                                >
                                    {copied ? 'Copied!' : 'Copy as Markdown'}
                                </button>
                            </div>
                            <div className="divide-y divide-slate-200 dark:divide-slate-700">
                                {checklist.map((acct) => (
                                    <div key={acct.utilityAccountNumber} className="px-4 py-3">
                                        <h4 className="text-sm font-medium text-slate-900 dark:text-white">
                                            {acct.accountName || 'Unknown Account'}
                                            <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400 font-mono">
                                                {acct.utilityAccountNumber}
                                            </span>
                                        </h4>
                                        <ul className="mt-2 space-y-1.5">
                                            {acct.actions.map((action, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                                                    <input
                                                        type="checkbox"
                                                        className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                    <span>{action}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </DisclosurePanel>
                    </>
                )}
            </Disclosure>
        </div>
    )
}

export default ReconciliationActionChecklist
